/**
 * @component ry-countdown
 * @category  display
 * @summary   Animated numeric countdown display (0–999). The visible number slides
 *            vertically whenever `value` changes, using a pure-CSS digit-strip
 *            technique adapted from daisyUI's `.countdown` component. Set the
 *            `value` attribute (statically or from JS) and the host element
 *            updates a single `--value` inline custom property that drives the
 *            animation. No timers, no children to manage.
 *
 * Vendored from daisyUI v5 `countdown.css` (MIT). Daisy's Tailwind utility
 * classes (`inline-flex`, `invisible`, `relative`, etc.) are inlined as plain
 * CSS rules. The `--value` integer is registered with `@property` so the CSS
 * `mod()` / `round()` math interpolates smoothly across digit boundaries.
 *
 * Original source:
 *   https://github.com/saadeghi/daisyui/blob/master/packages/daisyui/src/components/countdown.css
 * License: MIT — https://github.com/saadeghi/daisyui/blob/master/LICENSE
 *
 * @attribute value   number?   Integer between 0 and 999 (default `0`). Anything
 *                              outside that range is clamped via `max(0, …)` +
 *                              `mod(…, 1000)` in the CSS itself, mirroring
 *                              daisyUI's behavior. Each value change updates the
 *                              host's inline `style="--value: N"`.
 * @attribute digits  '1' | '2' | '3'?   Minimum digit slots to reserve (default
 *                              `1`). Daisy auto-grows on its own once the value
 *                              crosses 10/100; bumping `digits` forces leading
 *                              zeros (e.g. `digits="3"` always shows three).
 *
 * @property  value   number    Mirror of the `value` attribute. Setter writes the
 *                              attribute, which triggers the inline style update.
 *
 * @content   None — `<ry-countdown>` is configured entirely via attributes.
 *            innerHTML is replaced at setup with a single `<span>` child that
 *            owns the digit-strip pseudo-elements.
 *
 * @dom
 *   ry-countdown[value]                            style="--value: N"
 *     > span.ry-countdown__digit  [data-ry-target="digit"]
 *         (::before  — tens place, via content var(--tw-content))
 *         (::after   — ones place, via content var(--tw-content))
 *
 * @gotcha  `value` is a CSS integer, not text content. Editing the child span's
 *          textContent does nothing — only updating the host attribute (or the
 *          inline `--value` style) moves the digits. This is the daisy contract
 *          verbatim.
 *
 * @gotcha  Range is 0–999. Larger numbers wrap modulo 1000 (daisy behavior).
 *          Negative numbers are clamped to zero. For longer countdowns compose
 *          multiple `<ry-countdown>` instances (hours : minutes : seconds).
 *
 * @gotcha  The animation depends on `@property --value` being registered, which
 *          Safari ≥16.4, Chrome ≥85, Firefox ≥128 all support. Older browsers
 *          will show the number jump rather than slide — the digit is still
 *          legible, but the transition is missing.
 *
 * @example basic
 * ```html
 * <ry-countdown value="42"></ry-countdown>
 * ```
 *
 * @example zero
 * ```html
 * <ry-countdown value="0"></ry-countdown>
 * ```
 *
 * @example large
 * ```html
 * <ry-countdown value="999"></ry-countdown>
 * ```
 *
 * @example padded-three-digits
 * ```html
 * <ry-countdown value="7" digits="3"></ry-countdown>
 * ```
 *
 * @tutorial mount-reflects-value-into-inline-style
 * ```js
 * // The host element carries the --value custom property as an inline style.
 * // The CSS animation reads from there.
 * const el = mount('<ry-countdown value="42"></ry-countdown>');
 * await expectEqual(el.style.getPropertyValue('--value').trim(), '42', 'inline --value set on mount');
 * ```
 *
 * @tutorial value-change-updates-inline-style
 * ```js
 * // Updating the value attribute (the documented way to tick down) re-writes
 * // the inline --value style, which the CSS picks up and animates.
 * const el = mount('<ry-countdown value="10"></ry-countdown>');
 * el.setAttribute('value', '9');
 * await expectEqual(el.style.getPropertyValue('--value').trim(), '9', 'inline --value tracks attribute');
 * ```
 *
 * @tutorial default-value-is-zero
 * ```js
 * // Wrong assumption: omitting the value attribute throws or renders blank.
 * // It defaults to 0 — useful for ticking up from idle.
 * const el = mount('<ry-countdown></ry-countdown>');
 * await expectEqual(el.style.getPropertyValue('--value').trim(), '0', 'defaults to 0');
 * ```
 *
 * @tutorial property-setter-mirrors-attribute
 * ```js
 * // The .value JS property is a thin mirror over the attribute, so either
 * // path drives the same animation pipeline.
 * const el = mount('<ry-countdown value="5"></ry-countdown>');
 * el.value = 6;
 * await expectEqual(el.getAttribute('value'), '6', 'attribute reflects setter');
 * await expectEqual(el.style.getPropertyValue('--value').trim(), '6', 'inline style follows');
 * ```
 *
 * @see ry-stat
 */

import { RyElement } from '../core/ry-element.js';

export class RyCountdown extends RyElement {
  static observedAttributes = ['value'] as const;

  setup(): void {
    this.#transformMarkup();
    this.#syncValue();
  }

  attributeChangedCallback(name: string): void {
    if (name === 'value') this.#syncValue();
  }

  get value(): number {
    const raw = this.getAttribute('value');
    const n = raw == null ? 0 : Number(raw);
    return Number.isFinite(n) ? n : 0;
  }

  set value(next: number) {
    this.setAttribute('value', String(next));
  }

  #transformMarkup(): void {
    if (this.$('[data-ry-target="digit"]')) return;
    // Single child span owns the ::before / ::after pseudo-strips. The host
    // element carries --value; the strip inherits and animates from it.
    this.innerHTML = `<span data-ry-target="digit" class="ry-countdown__digit"></span>`;
  }

  #syncValue(): void {
    const raw = this.getAttribute('value');
    const n = raw == null ? 0 : Number(raw);
    const safe = Number.isFinite(n) ? n : 0;
    this.style.setProperty('--value', String(safe));
  }
}

customElements.define('ry-countdown', RyCountdown);
